import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useSocketStore } from './socket';
import { useAuthStore } from './auth';
import { apiFetch } from '../services/api';

export interface Camera {
  cameraId: string;
  deviceId: string;
  integration_id?: string;
  integrationId?: string;
  cameraName: string;
  hostname: string;
  port: number;
  username?: string;
  password?: string;
  enabled?: boolean;
  onvif_enabled?: boolean;
  onvifEnabled?: boolean;
  device_service_url?: string;
  deviceServiceUrl?: string;
  media_service_url?: string;
  mediaServiceUrl?: string;
  ptz_service_url?: string;
  ptzServiceUrl?: string;
  imaging_service_url?: string;
  imagingServiceUrl?: string;
  events_service_url?: string;
  eventsServiceUrl?: string;
  rtsp_url?: string;
  rtspUrl?: string;
  jpeg_snapshot_url?: string;
  jpegSnapshotUrl?: string;
  stream_profile?: string;
  streamProfile?: string;
  rtsp_transport?: string;
  rtspTransport?: string;
  has_ptz?: boolean;
  hasPtz?: boolean;
  has_audio?: boolean;
  hasAudio?: boolean;
  has_motion_detection?: boolean;
  hasMotionDetection?: boolean;
  recording_enabled?: boolean;
  recordingEnabled?: boolean;
  motion_recording_enabled?: boolean;
  motionRecordingEnabled?: boolean;
  recording_retention_days?: number;
  recordingRetentionDays?: number;
  connected: boolean;
  last_snapshot_at?: string;
  lastSnapshotAt?: string;
  last_motion_at?: string;
  lastMotionAt?: string;
  displayOrder?: number;
  hidden?: boolean;
  secret?: boolean;
  privacyBlurUntil?: string | null;
  privacyBlurActive?: boolean;
  motionNotificationsMutedUntil?: string | null;
  motionNotificationsMutedActive?: boolean;
  subStreamUrl?: string;
  subStreamProfile?: string;
  nvrChannel?: number | null;
  createdAt: string;
  updatedAt: string;
}

export const useCamerasStore = defineStore('cameras', () => {
  const socketStore = useSocketStore();
  const authStore = useAuthStore();

  // State
  const cameras = ref<Camera[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);
  const lastUpdated = ref<number>(0);
  const secretUnlocked = ref(false);
  const secretPinConfigured = ref<boolean | null>(null);

  // Computed getters
  const orderedCameras = computed(() =>
    [...cameras.value].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
  );

  const visibleCameras = computed(() => {
    const base = authStore.isAdmin ? orderedCameras.value : orderedCameras.value.filter((c) => !c.hidden);
    return base.filter((c) => !c.secret || secretUnlocked.value);
  });

  const hasSecretCameras = computed(() => cameras.value.some((c) => c.secret));

  const camerasByIntegration = computed(() => {
    const map = new Map<string, Camera[]>();
    cameras.value.forEach((camera) => {
      const integrationId = camera.integrationId || camera.integration_id;
      if (integrationId && !map.has(integrationId)) {
        map.set(integrationId, []);
      }
      if (integrationId) {
        map.get(integrationId)!.push(camera);
      }
    });
    return map;
  });

  const getCamera = (cameraId: string) => cameras.value?.find((c) => c.cameraId === cameraId);
  const getCamerasByIntegration = (integrationId: string) =>
    cameras.value?.filter((c) => c.integration_id === integrationId) || [];

  /**
   * Initialize cameras store - load all cameras
   */
  let initPromise: Promise<void> | null = null;

  async function initialize(): Promise<void> {
    if (loaded.value) return;
    if (initPromise) return initPromise;

    initPromise = (async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = authStore.isAdmin ? '?includeHidden=true' : '';
        const camerasData = await apiFetch(`/cameras${params}`);
        cameras.value = camerasData;

        loaded.value = true;
        lastUpdated.value = Date.now();

        console.log('[CamerasStore] Initialization complete');
        setupCameraListeners();
      } catch (__caught: unknown) {
        const err = __caught as {
          response?: {
            status?: number;
            data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
          };
        };
        error.value = err.response?.data?.error || 'Failed to load cameras';
        console.error('[CamerasStore] Failed to initialize:', err);
      } finally {
        loading.value = false;
        initPromise = null;
      }
    })();

    return initPromise;
  }

  /**
   * Refresh all cameras data
   */
  async function refresh() {
    console.log('[CamerasStore] Refreshing cameras...');
    loading.value = true;
    error.value = null;

    try {
      const params = authStore.isAdmin ? '?includeHidden=true' : '';
      const camerasData = await apiFetch(`/cameras${params}`);
      cameras.value = camerasData;

      console.log('[CamerasStore] Refresh complete');
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to refresh cameras';
      console.error('[CamerasStore] Failed to refresh:', err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Batch-update camera display order and secret status (admin only)
   */
  async function updateCameraOrder(
    updates: Array<{ cameraId: string; displayOrder: number; secret: boolean }>,
  ): Promise<void> {
    await apiFetch('/cameras/order', { method: 'PUT', body: JSON.stringify({ cameras: updates }) });
    updates.forEach(({ cameraId, displayOrder, secret }) => {
      updateCamera(cameraId, { displayOrder, secret });
    });
  }

  /**
   * Verify secret devices PIN; if valid, unlock secret cameras for this session.
   * Throws when the request itself fails (route missing, 500) so the caller can
   * tell "wrong PIN" apart from "the hub could not answer".
   */
  async function verifySecretPin(pin: string): Promise<boolean> {
    const result = await apiFetch('/system/secret-pin/verify', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
    const valid = result?.valid === true;
    if (valid) {
      secretUnlocked.value = true;
      await refresh();
    }
    return valid;
  }

  /**
   * Is a secret devices PIN set on this hub? (admin only)
   */
  async function loadSecretPinStatus(): Promise<boolean | null> {
    try {
      const result = await apiFetch('/system/secret-pin/status');
      secretPinConfigured.value = result?.configured === true;
      return secretPinConfigured.value;
    } catch (err) {
      console.error('[CamerasStore] Failed to read secret PIN status:', err);
      secretPinConfigured.value = null;
      return null;
    }
  }

  /**
   * Set (or replace) the secret devices PIN. The hub hashes it — it can never be
   * read back, so a forgotten PIN must be replaced, not recovered.
   */
  async function setSecretPin(pin: string): Promise<void> {
    await apiFetch('/system/secret-pin', {
      method: 'PUT',
      body: JSON.stringify({ pin }),
    });
    secretPinConfigured.value = true;
    secretUnlocked.value = true;
    await refresh();
  }

  /**
   * Set privacy blur on a camera (admin only)
   */
  async function setPrivacyBlur(cameraId: string, minutes: number): Promise<void> {
    const updated = await apiFetch(`/cameras/${cameraId}/privacy-blur`, {
      method: 'POST',
      body: JSON.stringify({ minutes }),
    });
    updateCamera(cameraId, updated);
  }

  async function setMotionNotificationsMuted(cameraId: string, minutes: number): Promise<void> {
    const updated = await apiFetch(`/cameras/${cameraId}/mute-motion-notifications`, {
      method: 'POST',
      body: JSON.stringify({ minutes }),
    });
    updateCamera(cameraId, updated);
  }

  /**
   * Check if data is stale (older than 30 seconds)
   */
  function isStale(): boolean {
    return Date.now() - lastUpdated.value > 30000;
  }

  // ===== Optimistic update helpers =====

  function addCamera(camera: Camera) {
    const exists = cameras.value.some((c) => c.cameraId === camera.cameraId);
    if (!exists) {
      cameras.value.push(camera);
    }
  }

  function updateCamera(cameraId: string, updates: Partial<Camera>) {
    const index = cameras.value.findIndex((c) => c.cameraId === cameraId);
    if (index !== -1) {
      cameras.value[index] = { ...cameras.value[index], ...updates };
    }
  }

  function removeCamera(cameraId: string) {
    const index = cameras.value.findIndex((c) => c.cameraId === cameraId);
    if (index !== -1) {
      cameras.value.splice(index, 1);
    }
  }

  /**
   * Drop all cached camera data when switching to a different hub so the new
   * hub's cameras are (re)fetched instead of showing the previous hub's list.
   */
  function resetForHubSwitch() {
    cameras.value = [];
    loaded.value = false;
    error.value = null;
    lastUpdated.value = 0;
    secretUnlocked.value = false;
    initPromise = null;
  }

  function setupCameraListeners() {
    if (!socketStore.socket) {
      console.warn('[CamerasStore] Socket not available, skipping camera listeners setup');
      return;
    }

    console.log('[CamerasStore] Setting up camera WebSocket listeners');

    // Camera events
    socketStore.socket.on('camera:created', (data: Camera) => {
      console.log('[CamerasStore] Camera created:', data.cameraId);
      addCamera(data);
      lastUpdated.value = Date.now();
    });

    socketStore.socket.on('camera:updated', (data: Camera) => {
      console.log('[CamerasStore] Camera updated:', data.cameraId);
      updateCamera(data.cameraId, data);
      lastUpdated.value = Date.now();
    });

    socketStore.socket.on('camera:deleted', (data: { cameraId: string }) => {
      console.log('[CamerasStore] Camera deleted:', data.cameraId);
      removeCamera(data.cameraId);
      lastUpdated.value = Date.now();
    });

    socketStore.socket.on(
      'camera:privacy_blur',
      (data: { cameraId: string; minutes: number; privacyBlurActive: boolean }) => {
        console.log('[CamerasStore] Privacy blur updated:', data.cameraId);
        const blurUntil = data.privacyBlurActive ? new Date(Date.now() + data.minutes * 60 * 1000).toISOString() : null;
        updateCamera(data.cameraId, { privacyBlurActive: data.privacyBlurActive, privacyBlurUntil: blurUntil });
        lastUpdated.value = Date.now();
      },
    );

    socketStore.socket.on(
      'camera:motion_notifications_muted',
      (data: { cameraId: string; minutes: number; motionNotificationsMutedActive: boolean }) => {
        console.log('[CamerasStore] Motion notification mute updated:', data.cameraId);
        const mutedUntil = data.motionNotificationsMutedActive
          ? new Date(Date.now() + data.minutes * 60 * 1000).toISOString()
          : null;
        updateCamera(data.cameraId, {
          motionNotificationsMutedActive: data.motionNotificationsMutedActive,
          motionNotificationsMutedUntil: mutedUntil,
        });
        lastUpdated.value = Date.now();
      },
    );
  }

  return {
    // State
    cameras,
    loading,
    loaded,
    error,
    lastUpdated,
    secretUnlocked,

    // Computed
    camerasByIntegration,
    orderedCameras,
    visibleCameras,
    hasSecretCameras,

    // Getters
    getCamera,
    getCamerasByIntegration,

    // Actions
    initialize,
    refresh,
    isStale,
    resetForHubSwitch,
    updateCameraOrder,
    verifySecretPin,
    loadSecretPinStatus,
    setSecretPin,
    setPrivacyBlur,
    setMotionNotificationsMuted,

    // Optimistic updates
    addCamera,
    updateCamera,
    removeCamera,
  };
});
